import { useQuery } from '@tanstack/react-query';
import CustomerContainer from '@tmh-website/components/containers/CustomerContainer';
import Footer from '@tmh-website/components/global/Footer';
import Header from '@tmh-website/components/global/Header';
import { fourOhFourPageData } from '@tmh-website/components/system/ErrorCodePage.config';
import { getGlobalData } from '@tmh-website/services/cms.client.service';
import { GlobalContext, PageContext, ProjectContext, ScrollContext, useRelevantAudience } from '@tmh-website/shared';
import { Locale, LocaleToPrefixMap, PrefixToLocaleMap, Project } from '@tmh-website/shared/const';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import GlobalStyles from './GlobalStyles';
import { fallbackGlobalData } from './cms-fallback';
import './i18n';

export function App() {
  const { i18n } = useTranslation();
  const [headerIsVisible, setHeaderIsVisible] = useState(false);
  const [localeSwitchBannerIsVisible, setLocaleSwitchBannerIsVisible] = useState(false);
  const [hasPageNavigation, setHasPageNavigation] = useState(false);
  const [lastScrollSectionChange, setLastScrollSectionChange] = useState(0);
  const locale = LocaleToPrefixMap[i18n.language as Locale];
  const { audience, audienceIsLoaded, setAudience } = useRelevantAudience({ locale });

  const { data: globalData } = useQuery(
    ['global-data', i18n.language],
    async () => {
      try {
        return await getGlobalData(Project.Website, locale);
      } catch (error) {
        strapi.plugin('sentry').service('sentry').sendError(error, { message: 'Error fetching global data' });
        console.error('Error fetching global data', error);
        return fallbackGlobalData[locale];
      }
    },
    {
      initialData: {
        id: 1,
        navigation: [],
        navigationHighlights: [],
        footerLinkGroups: [],
        footerLegalLinks: [],
        footerPaymentPartners: {
          id: 0,
          text: '',
          partnerLogosEntries: [],
        },
        footerPartners: {
          id: 0,
          text: '',
          partnerLogosEntries: [],
        },
        footerLogisticPartners: {
          id: 0,
          text: '',
          partnerLogosEntries: [],
        },
        footerCertificates: {
          id: 0,
          text: '',
          partnerLogosEntries: [],
        },
        footerContactSectionHeadline: '',
        footerContactSectionText: '',
        footerContactSectionLink: {},
        footerSpecialLink: {},
        socialLinks: [],
        metaTitle: '',
        metaDescription: '',
        metaImage: {
          id: 0,
          url: '',
          alternativeText: '',
          width: 0,
          height: 0,
          ext: '',
          hash: '',
          type: 'image',
        },
        locale: PrefixToLocaleMap[locale],
      },
    }
  );

  return (
    <ScrollContext.Provider
      value={{
        headerIsVisible,
        setHeaderIsVisible,
        hasPageNavigation,
        setHasPageNavigation,
        lastScrollSectionChange,
        setLastScrollSectionChange,
        localeSwitchBannerIsVisible,
        setLocaleSwitchBannerIsVisible,
      }}>
      <GlobalContext.Provider value={{ ...globalData, audience, setAudience, audienceIsLoaded }}>
        <PageContext.Provider value={fourOhFourPageData(locale)}>
          <ProjectContext.Provider value={{ project: Project.Website }}>
            <CustomerContainer locale={locale}>
              <GlobalStyles />
              <Header isStandaloneMode locale={locale} currentPath={window.location.href} />
              <Footer isStandaloneMode locale={locale} />
            </CustomerContainer>
          </ProjectContext.Provider>
        </PageContext.Provider>
      </GlobalContext.Provider>
    </ScrollContext.Provider>
  );
}

export default App;
